import React, { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useSprintPacket } from 'hooks/api/useSprintPackets'
import { useSprint } from 'hooks/api/useSprint'
import { ReviewCreativeCard } from './review/ReviewCreativeCard'
import { ReviewCreativePreviewModal } from './review/ReviewCreativePreviewModal'
import { usePacketFonts } from './usePacketFonts'
import { light, type as typo } from './tokens'

// The client-facing counterpart to `PresentSprintPacket`: instead of paging
// through the deck, this lists every ad in the sprint so the client can
// approve or reject each one directly (with a reason on rejection), via the
// same reviews endpoint the AE-attestation flow already uses.
export function ReviewSprintPacket() {
  usePacketFonts()
  const { businessID, packetID } = useParams<{ businessID: string; packetID: string }>()
  const businessIDNum = Number(businessID)

  const { packet, isLoading: packetLoading } = useSprintPacket(Number(packetID))
  const { sprintCreatives, sprintCreativesLoading } = useSprint(packet?.sprintID)

  const loading = packetLoading || sprintCreativesLoading
  const creatives = sprintCreatives ?? []

  // One atomic object rather than two separate `useState`s — `handleAdvance`
  // below fires after an `await` (a promise continuation, not a React event
  // handler), where React doesn't guarantee batching two separate `setState`
  // calls into a single render. With two states, that produced a real bug: a
  // render where the id had already moved to the next ad but the mode hadn't
  // caught up yet, mounting that ad's modal in reject mode by mistake — and
  // once mounted, its local `rejecting` state stuck there even once the mode
  // prop corrected itself a render later. One `setOpen` call can't split like
  // that.
  const [open, setOpen] = useState<{ creativeID: number; mode: 'view' | 'reject' } | null>(null)
  const openIndex = open ? creatives.findIndex(c => c.id === open.creativeID) : -1
  const openCreative = openIndex >= 0 ? creatives[openIndex] : null

  const openAt = (creativeID: number, mode: 'view' | 'reject' = 'view') =>
    setOpen({ creativeID, mode })

  // Successful approve/reject advances to the next ad (slideshow-style) —
  // whichever card comes next in the grid, regardless of which ad the client
  // started on — and only actually closes once there's no next ad left.
  const handleAdvance = () => {
    const next = creatives[openIndex + 1]
    setOpen(next ? { creativeID: next.id, mode: 'view' } : null)
  }

  // Manual back/forward through the deck, independent of approving/rejecting —
  // always lands in view mode.
  const goToPrev = () => {
    const prev = creatives[openIndex - 1]
    if (prev) setOpen({ creativeID: prev.id, mode: 'view' })
  }
  const goToNext = () => {
    const next = creatives[openIndex + 1]
    if (next) setOpen({ creativeID: next.id, mode: 'view' })
  }

  return (
    <div style={{ minHeight: '100vh', background: light.bg }}>
      <div style={{ maxWidth: 1080, margin: '0 auto', padding: '48px 32px' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 24,
          }}
        >
          <div>
            <div
              style={{
                fontFamily: typo.family.sans,
                fontSize: typo.size.eyebrow,
                fontWeight: typo.weight.semibold,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: light.accent,
                marginBottom: 8,
              }}
            >
              Client review
            </div>
            <h1
              style={{
                margin: 0,
                fontFamily: typo.family.serif,
                fontWeight: typo.weight.regular,
                fontSize: typo.size.h1,
                color: light.text,
              }}
            >
              Review ads
            </h1>
            <p
              style={{
                marginTop: 8,
                marginBottom: 0,
                fontFamily: typo.family.sans,
                fontSize: typo.size.bodyLg,
                color: light.textMuted,
              }}
            >
              {packet ? packet.sprint?.name ?? `Sprint #${packet.sprintID}` : ''}
            </p>
          </div>

          <Link
            to={`/${businessID}/sprint-packets/${packetID}/present`}
            style={{
              flexShrink: 0,
              marginTop: 4,
              fontFamily: typo.family.sans,
              fontSize: typo.size.body,
              fontWeight: typo.weight.semibold,
              color: light.accent,
              textDecoration: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            View full sprint packet →
          </Link>
        </div>

        <div style={{ marginBottom: 36 }} />

        {loading && (
          <div style={{ fontFamily: typo.family.sans, color: light.textMuted }}>Loading…</div>
        )}

        {!loading && creatives.length === 0 && (
          <div style={{ fontFamily: typo.family.sans, color: light.textMuted }}>
            There are no ads to review yet.
          </div>
        )}

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 24,
          }}
        >
          {creatives.map(creative => (
            <ReviewCreativeCard
              key={creative.id}
              creative={creative}
              businessID={businessIDNum}
              onOpen={() => openAt(creative.id)}
              onOpenReject={() => openAt(creative.id, 'reject')}
            />
          ))}
        </div>
      </div>

      {openCreative && (
        <ReviewCreativePreviewModal
          key={openCreative.id}
          businessID={businessIDNum}
          creative={openCreative}
          startInReject={open?.mode === 'reject'}
          hasPrev={openIndex > 0}
          hasNext={openIndex < creatives.length - 1}
          onPrev={goToPrev}
          onNext={goToNext}
          onAdvance={handleAdvance}
          onClose={() => setOpen(null)}
        />
      )}
    </div>
  )
}
